// app/shared/components/cards/HTFContentCard.tsx
"use client";

// app/shared/components/cards/HTFContentCard.tsx
import React from "react";

import { cn } from "@/lib/utils";

export type HTFContentCardVariant =
  | "default"
  | "primary"
  | "secondary"
  | "code"
  | "accent"
  | "muted"
  | "teal"
  | "yellow"
  | "error";

export interface HTFContentCardProps {
  children: React.ReactNode;
  variant?: HTFContentCardVariant;
  className?: string;
  padded?: boolean;
  bordered?: boolean;
  elevated?: boolean;
  title?: string;
  titleClassName?: string;
}

export function HTFContentCard({
  children,
  variant = "default",
  className,
  padded = true,
  bordered = true,
  elevated = false,
  title,
  titleClassName,
}: HTFContentCardProps) {
  // Variant style mappings
  const variantStyles: Record<HTFContentCardVariant, string> = {
    default: "bg-white",
    primary: "bg-primary/10",
    secondary: "bg-secondary/10",
    code: "bg-[#F0EBE6] font-mono text-sm",
    accent: "bg-[#E2DACB]",
    muted: "bg-gray-50", // Use a very light gray for muted
    teal: "bg-teal-500/10", // Adjusted teal background
    yellow: "bg-yellow-400/10", // Adjusted yellow background
    error: "bg-red-50", // Background for error
  };

  const borderStyles: Record<HTFContentCardVariant, string> = {
    default: "border-gray-200",
    primary: "border-primary/20",
    secondary: "border-secondary/20",
    code: "border-[#E2DACB]/60",
    accent: "border-[#E2DACB]/80",
    muted: "border-gray-200", // Muted border
    teal: "border-teal-500/20", // Adjusted teal border
    yellow: "border-yellow-400/20", // Adjusted yellow border
    error: "border-red-300", // Border for error
  };

  return (
    <div className={cn("mb-4", title && "space-y-2")}>
      {title && (
        <h4 className={cn("text-sm font-medium text-gray-700", titleClassName)}>
          {title}
        </h4>
      )}

      <div
        className={cn(
          "rounded-md",
          variantStyles[variant],
          bordered && `border ${borderStyles[variant]}`,
          padded && "p-3",
          elevated && "shadow-sm",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}
